/**
 * How much an installer records about materials — three states, not a boolean.
 *
 * The middle state is why this exists. A shop that wants to know WHAT was used
 * but does not maintain stock levels previously had no correct path: the capture
 * modal required an inventory item, so recording a usage meant inventing a
 * receipt, which then accumulated stock records the shop was not maintaining.
 *
 * The distinction cannot be inferred at capture time. An empty stock list means
 * "we don't track stock" to one shop and "you forgot to pick the roll" to
 * another — same state, opposite correct answers — so it has to be a setting.
 *
 * LABELS ARE OURS, VALUES ARE THE SERVER'S ENUM. Kept separate deliberately:
 * short lowercase values age well in a column, labels get reworded. Never send
 * a label.
 *
 * The server mirrors the legacy `materials_tracking_enabled` boolean from this
 * mode (`mode !== 'none'`) and is its only writer, so the two cannot drift —
 * but either may be absent depending on whether the migration has run, which is
 * what `materialsMode` falls back for.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/materialsTracking.test.mjs`. Do not add imports.
 */

export const MATERIALS_TRACKING_OPTIONS = [
  {
    value: 'none',
    label: 'No Tracking',
    description: 'Technicians are not asked about materials.',
  },
  {
    value: 'usage',
    label: 'Record Used',
    description: 'Record the product and how much was used. Stock levels are not tracked or reduced.',
  },
  {
    value: 'inventory',
    label: 'Record Used from Stock',
    description: 'Record which roll or batch was used, and reduce its remaining quantity. Requires keeping stock up to date.',
  },
];

/**
 * The installer's mode, falling back to the boolean it is mirrored from.
 *
 * The boolean's own historical default was "on", which is why an absent one
 * reads as `inventory` — that was the only behaviour before this setting
 * existed, so treating it as anything else would silently change what an
 * un-migrated installer sees.
 */
export function materialsMode(installer) {
  const mode = installer?.materials_tracking_mode;
  if (mode === 'none' || mode === 'usage' || mode === 'inventory') return mode;
  return installer?.materials_tracking_enabled !== false ? 'inventory' : 'none';
}

/** The option row for an installer — never undefined, so callers can read `.label`. */
export function materialsOption(installer) {
  const mode = materialsMode(installer);
  return MATERIALS_TRACKING_OPTIONS.find((o) => o.value === mode) || MATERIALS_TRACKING_OPTIONS[0];
}

/** Does this installer track stock levels, i.e. pick a roll and deduct from it? */
export function tracksInventory(installer) {
  return materialsMode(installer) === 'inventory';
}

/** Should technicians be asked about materials at all? */
export function collectsMaterials(installer) {
  return materialsMode(installer) !== 'none';
}
